'use client'

import { useState } from 'react'

import Autoplay from 'embla-carousel-autoplay'

import { ITEM_TITLE } from '@/lib/typography'

import { MaybeHtml } from '@/components/blocks/maybe-html'
import { Card, CardContent } from '@/components/ui/card'
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { MotionPreset } from '@/components/ui/motion-preset'
import { cn } from '@/lib/utils'

export type ImageAccordionItem = {
  image: string
  title: string
  description?: string
}

/**
 * Хулгана хүргэхэд өргөсдөг зургийн эгнээ.
 *
 * Хэлбэр, эффект нь pureskin загварын About → Our Team блокоос авагдсан:
 * зурагнууд тэнцүү нарийн багана болж зэрэгцэх бөгөөд аль нэг дээр нь хулгана
 * хүргэхэд `flex-grow` нь удаанаар (1s) томорч, доороос нь тайлбарын карт
 * гулсан гарч ирнэ.
 *
 * ⚠️ Хулганагүй төхөөрөмж дээр hover ажиллахгүй тул `md`-ээс доош
 * автоматаар эргэлддэг карусель болж солигдоно — эс бөгөөс утсан дээр
 * зөвхөн нарийхан зураас харагдана.
 *
 * ! Зураг `loading='lazy'` — эс бөгөөс React SSR бүгдийг нь `<head>`-д
 * ! preload болгож (нүүрэнд ~5.7 МБ), слайдерын видеотой өрсөлддөг байв.
 * ! Нуугдсан хувилбарын (`md:hidden` / `hidden md:flex`) зураг ч татагдахгүй.
 */
export const ImageAccordion = ({
  items,
  expandCenter = false
}: {
  items: ImageAccordionItem[]

  /** Хулгана хүрээгүй үед дунд зургийг хоёр дахин өргөн байлгана */
  expandCenter?: boolean
}) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const [autoplay] = useState(() => Autoplay({ delay: 2500, stopOnInteraction: false }))

  if (items.length === 0) {
    return null
  }

  /* Хулгана хүрээгүй эхний төлөв — голын зураг өргөссөн байна */
  const center = expandCenter ? Math.floor((items.length - 1) / 2) : null
  const active = hovered ?? center

  return (
    <Card className='bg-primary/10 shadow-none ring-0'>
      <CardContent className='hidden h-70 gap-4 sm:h-100 md:flex lg:h-125' onMouseLeave={() => setHovered(null)}>
        {items.map((item, index) => {
          const isActive = active === index

          return (
            <MotionPreset
              key={item.title}
              fade
              blur
              slide={{ direction: 'down', offset: 100 }}
              transition={{ duration: 0.5 }}
              delay={index * 0.15}
              className={cn(
                'relative flex h-full shrink-0 basis-0 transform-gpu cursor-pointer items-center justify-center overflow-hidden rounded-4xl transition-[flex-grow] duration-1000 ease-in-out will-change-transform',
                !isActive ? 'grow' : hovered === index ? 'grow-[4]' : 'grow-[2]'
              )}
              motionProps={{ onMouseEnter: () => setHovered(index) }}
            >
              <img src={item.image} alt={item.title} loading='lazy' className='size-full object-cover' />

              {/* Тайлбарыг уншигдахуйц болгох бүдэгрүүлэлт */}
              <div
                className={cn(
                  'absolute inset-x-0 bottom-0 h-40 rounded-b-4xl mask-[linear-gradient(to_top,black_40%,transparent)] backdrop-blur-md transition-opacity duration-500 ease-out sm:h-58',
                  !isActive ? 'opacity-0' : hovered === index ? 'opacity-100 delay-500' : 'opacity-100'
                )}
              />

              <div
                className={cn(
                  'bg-card absolute inset-x-3 bottom-0 rounded-4xl p-4 shadow-lg transition-transform duration-300 ease-out',
                  !isActive
                    ? 'pointer-events-none translate-y-full'
                    : hovered === index
                      ? '-translate-y-3 delay-700'
                      : '-translate-y-3'
                )}
              >
                <p className={cn('text-card-foreground', ITEM_TITLE)}>{item.title}</p>

                {/* Тайлбар нь CMS-д rich editor — форматтай ч, энгийн текст ч байж болно */}
                <MaybeHtml text={item.description} className='text-muted-foreground mt-1 hidden text-xl sm:block' />
              </div>
            </MotionPreset>
          )
        })}
      </CardContent>

      <CardContent className='md:hidden'>
        <Carousel
          opts={{ loop: true }}
          plugins={[autoplay]}
          onMouseEnter={autoplay.stop}
          onMouseLeave={() => autoplay.reset()}
        >
          <CarouselContent>
            {items.map(item => (
              <CarouselItem key={item.title} className='basis-full'>
                <div className='relative overflow-hidden rounded-4xl'>
                  <img src={item.image} alt={item.title} loading='lazy' className='aspect-3/4 w-full object-cover' />
                  <div className='bg-card absolute inset-x-3 bottom-3 rounded-xl p-3 shadow-lg'>
                    <p className={cn('text-card-foreground', ITEM_TITLE)}>{item.title}</p>
                    <MaybeHtml text={item.description} className='text-muted-foreground mt-1 text-xl' />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </CardContent>
    </Card>
  )
}
